import { memo, useCallback, useMemo } from 'react';
import { Pressable } from 'react-native';
import { Box, useHover } from '@betty/moonlight';
import { GameProps } from './../Game/Game.types';
import {
  useDispatch,
  useSelector,
  selectAuthenticationStatus,
} from '@betty/toolkit/state';
import { launchGame } from '../../../state';
import { getLockedLabel } from './Game.utils';
import JackpotLabel from './JackpotLabel/JackpotLabel';
import FavoritesToggle from './FavoritesToggle/FavoritesToggle';
import LockedLabel from './LockedLabel/LockedLabel';
import Thumbnail from './Thumbnail/Thumbnail';
import { selectGameReleaseDate } from '../../../state';
import Info from './Info/Info';

function Game({
  id,
  imageType,
  isCoinsGameplay,
  dynamicSize,
  currencyCode,
  onPress,
  highlighted,
  ...rest
}: GameProps) {
  const dispatch = useDispatch();
  const authStatus = useSelector(selectAuthenticationStatus);
  const releaseDate = useSelector(selectGameReleaseDate(id));
  const { isHovered, ...hoverTriggers } = useHover();
  const lockedLabel = useMemo(() => getLockedLabel(releaseDate), [releaseDate]);

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress(id);
    } else {
      dispatch(launchGame({ id, currencyCode }));
    }
  }, [onPress, id, dispatch, currencyCode]);

  return (
    <Box {...rest}>
      <Box
        overflow="hidden"
        borderRadius="radius-400"
        transform={isHovered ? 'scale(1.05)' : 'scale(1)'}
        {...(highlighted && {
          borderWidth: 'border-width-050',
          borderColor: 'borderGoldSolid',
        })}
      >
        <Pressable onPress={handlePress} {...hoverTriggers}>
          <Thumbnail gameId={id} type={imageType} dynamicSize={dynamicSize} />
        </Pressable>
        {!isCoinsGameplay && (
          <>
            <JackpotLabel gameId={id} imageType={imageType} />
            {authStatus === 'authenticated' ? (
              <FavoritesToggle gameId={id} imageType={imageType} />
            ) : (
              <Info gameId={id} />
            )}
          </>
        )}
      </Box>
      {lockedLabel ? <LockedLabel label={lockedLabel} /> : null}
    </Box>
  );
}

export default memo(Game);
